import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { useMailStore } from '../../stores/mailStore';

const LabelsSettings: React.FC = () => {
  const { labels, fetchLabels, createLabel, deleteLabel, updateLabel } = useMailStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<{ id: string; name: string; color: string } | null>(null);
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState('#1a73e8');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingLabelId, setDeletingLabelId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const colorOptions = [
    '#ea4335', '#fbbc05', '#34a853', '#4285f4', '#9c27b0',
    '#ff5722', '#795548', '#607d8b', '#00bcd4', '#e91e63',
    '#673ab7', '#3f51b5', '#009688', '#8bc34a', '#ff9800',
  ];

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const handleOpenDialog = (label?: { id: string; name: string; color: string }) => {
    if (label) {
      setEditingLabel(label);
      setLabelName(label.name);
      setLabelColor(label.color);
    } else {
      setEditingLabel(null);
      setLabelName('');
      setLabelColor('#1a73e8');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLabel(null);
    setLabelName('');
    setLabelColor('#1a73e8');
  };

  const handleSaveLabel = async () => {
    setIsLoading(true);
    try {
      if (editingLabel) {
        await updateLabel(editingLabel.id, labelName, labelColor);
        setSnackbar({ open: true, message: 'Label updated successfully', severity: 'success' });
      } else {
        await createLabel(labelName, labelColor);
        setSnackbar({ open: true, message: 'Label created successfully', severity: 'success' });
      }
      handleCloseDialog();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to save label', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeletingLabelId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingLabelId) return;
    setDeleteConfirmOpen(false);
    setIsLoading(true);
    try {
      await deleteLabel(deletingLabelId);
      setSnackbar({ open: true, message: 'Label deleted successfully', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to delete label', severity: 'error' });
    } finally {
      setIsLoading(false);
      setDeletingLabelId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 1 }}>
        Labels
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create labels to categorize and organize your emails
      </Typography>

      {/* Add label button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Label
        </Button>
      </Box>

      {/* Labels list */}
      <Box>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
          MY LABELS
        </Typography>
        {labels.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              No labels created. Create a label to organize your emails.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 1 }}>
            <List disablePadding>
              {labels.map((label, index) => (
                <ListItem
                  key={label.id}
                  secondaryAction={
                    <>
                      <IconButton onClick={() => handleOpenDialog(label)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteClick(label.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  }
                  sx={{
                    borderBottom: index < labels.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <LabelIcon sx={{ color: label.color, mr: 2 }} />
                  <ListItemText 
                    primary={label.name}
                    secondary={`Color: ${label.color}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* Label dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingLabel ? 'Edit Label' : 'Create Label'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Label name"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
              Label color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {colorOptions.map((color) => (
                <Box
                  key={color}
                  onClick={() => setLabelColor(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: color,
                    cursor: 'pointer',
                    border: labelColor === color ? '3px solid' : '2px solid transparent',
                    borderColor: labelColor === color ? 'text.primary' : 'transparent',
                    '&:hover': { opacity: 0.8 },
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveLabel}
            disabled={!labelName.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {editingLabel ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Label</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this label? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabelsSettings;
